<template>
  <v-dialog v-model="show" max-width="560" @keydown.esc="cancel">
    <v-card v-if="seller">
      <v-card-title>Seller Rating: {{ seller.eBayStoreName || seller.userID }}</v-card-title>
      <v-card-subtitle>
        {{ seller.userID }}
        <span v-if="seller.feedbackScore != null"> &middot; Feedback {{ seller.feedbackScore }}</span>
        <span v-if="seller.positiveFeedbackPercent != null"> ({{ seller.positiveFeedbackPercent }}%)</span>
      </v-card-subtitle>
      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-select label="Tier" v-model="tier" :items="tiers"
            hint="Excluded sellers' listings are hidden and never counted in snapshot statistics." persistent-hint />
          <v-text-field label="Price weight" v-model.number="weight" type="number" step="0.1" :min="minWeight" :max="maxWeight"
            :rules="[weightRule]"
            hint="Multiplies this seller's quantity in weighted average prices. 1 = normal." persistent-hint class="mt-4" />
          <v-textarea label="Notes" v-model="notes" rows="3" auto-grow counter="4000" :rules="[notesRule]" class="mt-4" />
          <div v-if="seller.updatedBy" class="caption grey--text">
            Last updated by {{ seller.updatedBy }} on {{ new Date(seller.updatedDateTime).toLocaleString() }}
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!valid" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { SellersService, SELLER_TIERS } from '../../services/api/sellers';
const apiSellers = new SellersService();

const MIN_WEIGHT = 0.01;
const MAX_WEIGHT = 10;

export default {
  data() {
    return {
      show: false,
      seller: null,
      tier: 'Normal',
      weight: 1,
      notes: '',
      valid: true,
      saving: false,
      tiers: SELLER_TIERS,
      minWeight: MIN_WEIGHT,
      maxWeight: MAX_WEIGHT,
      resolve: null,
    };
  },

  watch: {
    // Closing by clicking outside the dialog counts as cancelling.
    show(value) {
      if (!value && this.resolve) {
        this.resolve(null);
        this.resolve = null;
      }
    },
  },

  methods: {
    /**
     * Opens the dialog for the seller.  Resolves with the updated seller, or null if cancelled.
     */
    async open(sellerID) {
      this.seller = await apiSellers.getSeller(sellerID);
      this.tier = this.seller.tier || 'Normal';
      this.weight = this.seller.weight != null ? this.seller.weight : 1;
      this.notes = this.seller.notes || '';
      this.show = true;
      return new Promise((resolve) => {
        this.resolve = resolve;
      });
    },

    weightRule(value) {
      const number = Number(value);
      return (!Number.isNaN(number) && number >= MIN_WEIGHT && number <= MAX_WEIGHT)
        || `Weight must be between ${MIN_WEIGHT} and ${MAX_WEIGHT}`;
    },

    notesRule(value) {
      return !value || value.length <= 4000 || 'Notes must be 4000 characters or fewer';
    },

    async save() {
      this.saving = true;
      try {
        const updated = await apiSellers.updateSellerSettings(this.seller.id, {
          tier: this.tier,
          weight: Number(this.weight),
          notes: this.notes,
        });
        this.$notify({ type: 'success', title: 'Seller rating saved' });
        this.close(updated);
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.close(null);
    },

    close(result) {
      this.show = false;
      if (this.resolve) {
        this.resolve(result);
        this.resolve = null;
      }
    },
  },
};
</script>
